/**
 * Device enrollment — every install (mobile / PWA / web browser) registers
 * itself with the OC_Devices SharePoint list on first authenticated load,
 * stays Pending until an Admin Approves it from Settings → Devices.
 *
 * The list is optional. If OC_Devices doesn't exist yet (older sites or
 * first-time provisioning), every function here is a no-op so the app
 * keeps working — admins enable enforcement once they've run the
 * provisioning script.
 */
import { type Client } from '@microsoft/microsoft-graph-client';
import {
  getListItems,
  createListItem,
  updateListItem,
} from './sharePointService';
import type {
  DeviceEnrollment,
  DeviceStatus,
  ListIds,
  SPListItem,
} from '@/models/types';
import { secureGet, secureSet, isNativeShell, platform } from '@/utils/nativeBridge';

const DEVICE_ID_KEY = 'prism_device_id';
const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '1.0.0';

/** Stable per-install UUID. Generated once and cached forever. */
async function getOrCreateDeviceId(): Promise<string> {
  let id = await secureGet(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    await secureSet(DEVICE_ID_KEY, id);
  }
  return id;
}

interface DeviceFingerprint {
  deviceId: string;
  platform: string;
  model: string;
  osVersion: string;
  installSource: string;
  userAgent: string;
}

/**
 * Best-effort device fingerprint. Pulls from Capacitor Device plugin on
 * native, falls back to user-agent parsing on web/PWA.
 */
export async function getDeviceFingerprint(): Promise<DeviceFingerprint> {
  const deviceId = await getOrCreateDeviceId();
  const ua = navigator.userAgent;

  if (isNativeShell()) {
    try {
      const { Device } = await import('@capacitor/device');
      const info = await Device.getInfo();
      const installSource =
        info.platform === 'ios'
          ? // crude: TestFlight builds carry a sandbox receipt; we don't have
            // direct visibility client-side, so default "Native iOS"
            'Native iOS'
          : info.platform === 'android'
            ? 'Native Android'
            : 'Native';
      return {
        deviceId,
        platform: info.platform,
        model: info.model || 'unknown',
        osVersion: info.osVersion || 'unknown',
        installSource,
        userAgent: ua,
      };
    } catch (err) {
      console.warn('[Prism] Device plugin failed, falling back:', err);
    }
  }

  // Web / PWA fallback
  const isPWA =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari standalone
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  const installSource = isPWA ? 'PWA' : 'Web Browser';

  // Naive UA parse — good enough for an audit trail
  const platformGuess =
    /iPhone|iPad|iPod/i.test(ua)
      ? 'ios'
      : /Android/i.test(ua)
        ? 'android'
        : /Macintosh/i.test(ua)
          ? 'macos'
          : /Windows/i.test(ua)
            ? 'windows'
            : platform();
  const modelMatch =
    ua.match(/iPhone OS [\d_]+/) ??
    ua.match(/Android [\d.]+/) ??
    ua.match(/Mac OS X [\d_]+/) ??
    ua.match(/Windows NT [\d.]+/);
  return {
    deviceId,
    platform: platformGuess,
    model: modelMatch?.[0] ?? 'unknown',
    osVersion: modelMatch?.[0] ?? 'unknown',
    installSource,
    userAgent: ua,
  };
}

/**
 * Register or update this device's record. Idempotent — call on every
 * authenticated app start.
 *
 * Returns the live record so callers can inspect status.
 *
 * If OC_Devices isn't provisioned, returns null (caller treats as "no
 * enforcement configured" → allow).
 */
export async function enrollOrTouchDevice(
  client: Client,
  listIds: ListIds,
  email: string,
  displayName: string,
): Promise<SPListItem<DeviceEnrollment> | null> {
  if (!listIds.OC_Devices) {
    // List doesn't exist on this site — feature is off.
    return null;
  }

  const fingerprint = await getDeviceFingerprint();
  const now = new Date().toISOString();

  try {
    // Find existing record for (email, deviceId)
    const existing = await getListItems<DeviceEnrollment>(client, 'OC_Devices', {
      filter: `fields/Email eq '${email.replace(
        /'/g,
        "''",
      )}' and fields/DeviceId eq '${fingerprint.deviceId}'`,
      top: 1,
    });

    if (existing.length > 0) {
      // Touch LastSeen + AppVersion + UA (which can change post-update)
      const item = existing[0];
      await updateListItem<DeviceEnrollment>(client, 'OC_Devices', item.id, {
        LastSeen: now,
        AppVersion: APP_VERSION,
        UserAgent: fingerprint.userAgent,
      });
      return {
        ...item,
        fields: {
          ...item.fields,
          LastSeen: now,
          AppVersion: APP_VERSION,
          UserAgent: fingerprint.userAgent,
        },
      };
    }

    // First time we've seen this (user, device) pair
    const created = await createListItem<DeviceEnrollment>(
      client,
      'OC_Devices',
      {
        Title: `${fingerprint.model} — ${displayName}`,
        Email: email,
        DeviceId: fingerprint.deviceId,
        Platform: fingerprint.platform,
        Model: fingerprint.model,
        OSVersion: fingerprint.osVersion,
        AppVersion: APP_VERSION,
        InstallSource: fingerprint.installSource,
        FirstSeen: now,
        LastSeen: now,
        IPAddress: '', // server-side only — left blank client-side
        UserAgent: fingerprint.userAgent,
        Status: 'Pending',
        ApprovedBy: '',
        ApprovedAt: '',
        Notes: '',
      },
    );
    return created;
  } catch (err) {
    console.error('[Prism] Device enrollment failed:', err);
    return null;
  }
}

/** Admin action — flip a device's status (Approved / Blocked / Pending). */
export async function setDeviceStatus(
  client: Client,
  itemId: string,
  status: DeviceStatus,
  approverEmail: string,
): Promise<void> {
  await updateListItem<DeviceEnrollment>(client, 'OC_Devices', itemId, {
    Status: status,
    ApprovedBy: approverEmail,
    ApprovedAt: new Date().toISOString(),
  });
}
